"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import SocialLoginButtons from "./SocialLoginButtons";
import { rememberBrowserAuthHint } from "@/lib/auth-hint";
import type { OAuthProvider } from "@/lib/oauth/config";
import { toLocalizedPath, type Locale } from "@/lib/i18n";

type EnabledProviders = Record<OAuthProvider, boolean>;

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  oauth_state: "로그인 연결이 만료됐어요. 다시 시도해 주세요.",
  provider_unavailable:
    "소셜 로그인이 일시적으로 어려워요. 잠시 후 다시 시도해 주세요.",
  token_exchange_failed:
    "로그인 처리 중 문제가 생겼어요. 다시 시도해 주세요.",
  email_missing:
    "이 소셜 계정에서 이메일을 받아올 수 없어요. 이메일을 직접 입력해 주세요.",
  email_conflict_social:
    "이 이메일은 다른 소셜 계정으로 가입돼 있어요. 해당 소셜로 먼저 로그인해 주세요.",
  email_conflict_local_password:
    "이미 이 이메일로 가입된 계정이 있어요. 비밀번호로 로그인 후 연결해 주세요.",
  banned: "이용이 제한된 계정이에요. 문의해 주세요.",
  rate_limited: "잠시 후 다시 시도해 주세요.",
  link_failed: "소셜 계정 연결에 실패했어요. 다시 시도해 주세요.",
  oauth_already_linked_other:
    "이 소셜 계정은 이미 다른 사용자 계정에 연결돼 있어요.",
  storage_unavailable:
    "일시적인 서버 오류예요. 잠시 후 다시 시도해 주세요.",
};

type Props = {
  redirectTo: string;
  locale?: Locale;
  enabled?: EnabledProviders;
  errorKind?: string | null;
};

export default function LoginForm({
  redirectTo,
  locale = "ko",
  enabled = { google: false, kakao: false, naver: false, apple: false },
  errorKind = null,
}: Props) {
  const { refresh, replace } = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const isEn = locale === "en";
  const initialBanner = errorKind
    ? isEn
      ? {
          oauth_state: "Your sign-in connection expired. Please try again.",
          provider_unavailable: "Social sign-in is temporarily unavailable. Please try again later.",
          token_exchange_failed: "Sign-in failed. Please try again.",
          email_missing: "We could not get an email from this social account. Please enter it yourself.",
          email_conflict_social: "This email is already linked to another social account.",
          email_conflict_local_password: "An account already uses this email. Sign in with your password to connect it.",
        }[errorKind] ?? null
      : OAUTH_ERROR_MESSAGES[errorKind] ?? null
    : null;
  const [error, setError] = useState<string | null>(initialBanner);
  const [isPending, startTransition] = useTransition();
  const showSocial = Object.values(enabled).some(Boolean);
  const copy = isEn
    ? {
        missing: "Enter your email and password.", password: "Password must be at least 8 characters.",
        expired: "Your sign-in connection expired. Please try again.", unavailable: "Social sign-in is temporarily unavailable. Please try again later.",
        failed: "Sign-in failed. Please try again.", network: "A network error occurred. Please try again later.", tooMany: "Too many sign-in attempts. Please try again later.", invalid: "Your email or password is incorrect.", email: "Email", passwordLabel: "Password", divider: "or continue with email", submit: "Log in", pending: "Logging in…", noAccount: "Don’t have an account?", signup: "Sign up", forgot: "Forgot your password?", reset: "Reset password", terms: "Terms of Service", privacy: "Privacy Policy",
      }
    : {
        missing: "이메일과 비밀번호를 모두 입력해 주세요.", password: "비밀번호는 8자 이상이어야 해요.", expired: "로그인 연결이 만료됐어요. 다시 시도해 주세요.", unavailable: "소셜 로그인이 일시적으로 어려워요. 잠시 후 다시 시도해 주세요.", failed: "로그인 처리 중 문제가 생겼어요. 다시 시도해 주세요.", network: "네트워크 오류가 발생했어요. 잠시 후 다시 시도해 주세요.", tooMany: "로그인 시도가 많습니다. 잠시 후 다시 시도해 주세요.", invalid: "이메일 또는 비밀번호가 올바르지 않습니다.", email: "이메일", passwordLabel: "비밀번호", divider: "또는 이메일로 로그인", submit: "로그인", pending: "로그인 중…", noAccount: "아직 계정이 없나요?", signup: "회원가입", forgot: "비밀번호를 잊으셨나요?", reset: "재설정 요청", terms: "이용약관", privacy: "개인정보처리방침",
      };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || !password) {
      setError(copy.missing);
      return;
    }
    setError(null);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (res.ok) {
        const data = (await res.json().catch(() => null)) as { role?: string } | null;
        const target =
          redirectTo === "/" && data?.role === "ADMIN" ? "/admin" : redirectTo;
        rememberBrowserAuthHint();
        startTransition(() => {
          replace(target);
          refresh();
        });
        return;
      }
      if (res.status === 429) {
        setError(copy.tooMany);
        return;
      }
      setError(copy.invalid);
    } catch {
      setError(copy.network);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex w-full max-w-sm flex-col gap-4 rounded-3xl bg-white/80 p-6 shadow-pop"
      noValidate
    >
      {showSocial && (
        <>
          <SocialLoginButtons
            mode="login"
            redirectTo={redirectTo}
            enabled={enabled}
            locale={locale}
          />
          <div className="relative my-1 flex items-center text-xs text-slate-400">
            <span className="flex-1 border-t border-slate-200" />
            <span className="mx-3">{copy.divider}</span>
            <span className="flex-1 border-t border-slate-200" />
          </div>
        </>
      )}

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.email}
        <input
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isPending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.passwordLabel}
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isPending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>
      {error && (
        <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={isPending}
        className="rounded-xl bg-brand-gradient py-2 text-sm font-medium text-white shadow-pop disabled:opacity-60"
      >
        {isPending ? copy.pending : copy.submit}
      </button>

      <p className="text-center text-xs text-slate-500">
        {copy.noAccount}{" "}
        <Link
          href={
            redirectTo === "/"
              ? toLocalizedPath("/signup", locale)
              : `${toLocalizedPath("/signup", locale)}?from=${encodeURIComponent(redirectTo)}`
          }
          className="font-medium text-brand-600 underline"
        >
          {copy.signup}
        </Link>
      </p>
      <p className="text-center text-xs text-slate-400">
        {copy.forgot}{" "}
        <Link href={toLocalizedPath("/password-reset-request", locale)} className="underline">
          {copy.reset}
        </Link>
      </p>
      <p className="text-center text-xs text-slate-400">
        <Link href={toLocalizedPath("/legal/terms", locale)} className="underline">
          {copy.terms}
        </Link>
        <span className="mx-1 text-slate-300">·</span>
        <Link href={toLocalizedPath("/legal/privacy", locale)} className="underline">
          {copy.privacy}
        </Link>
      </p>
    </form>
  );
}
